import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Image, rem, Skeleton, useDarkMode } from '@bonusly/design-system';

import darkLogo from '../../../../assets/images/bonusly-logo-dark-mode.png';
import { useSeasonalLogo } from '../../../../components/seasonal/use-seasonal-logo';
import logo from '../../assets/images/bonusly-logo.png';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';

export const CompanyLogo: FC = () => {
	const { company, isLoading } = useCompanyProfile();
	const { t } = useTranslation('navigation');
	const { logoUrl: seasonalLogo } = useSeasonalLogo();
	const { isDarkMode } = useDarkMode();

	// Determine which logo to use: seasonal > company > default (with dark mode support)
	const logoConfig = useMemo(() => {
		// Priority: seasonal theme > company logo > bonusly logo (light/dark)
		const defaultLogo = isDarkMode ? darkLogo : logo;
		const logoSrc = seasonalLogo || company?.logo_url || defaultLogo;
		const logoAlt = seasonalLogo
			? t('links.company_logo', { name: 'Bonusly 🎃' })
			: company?.logo_url
				? t('links.company_logo', { name: company.name })
				: t('links.company_logo', { name: 'Bonusly' });

		return { logoSrc, logoAlt };
	}, [seasonalLogo, company?.logo_url, company?.name, t, isDarkMode]);

	if (isLoading) {
		return <Skeleton borderRadius="md" data-testid="logo-loading" h={rem(40)} w={rem(120)} />;
	}

	return <Image alt={logoConfig.logoAlt} h="full" objectFit="contain" src={logoConfig.logoSrc} />;
};
